import { BusinessNameGeneratorContainer } from '@/React/FreeTool/BusinessNameGenerator/Component/BusinessNameGeneratorContainer'
import { BusinessNameGeneratorEmptySuggestionsCard } from '@/React/FreeTool/BusinessNameGenerator/Component/BusinessNameGeneratorEmptySuggestionsCard'
import { BusinessNameGeneratorSuggestedNameCard } from '@/React/FreeTool/BusinessNameGenerator/Component/BusinessNameGeneratorSuggestedNameCard'
import { BusinessNameGeneratorUserPromptSummary } from '@/React/FreeTool/BusinessNameGenerator/Component/BusinessNameGeneratorUserPromptSummary'
import { FreeToolLayout } from '@/React/FreeTool/Shared/Layout/FreeToolLayout'
import { Button } from '@/React/Shared/Component/Button'
import { IconNameEnum } from '@/React/Shared/Component/Icon'
import {
    type BEShowBusinessNameGeneratorStepResultsPagePropKeys,
    type BusinessNameGeneratorSessionRawData,
    type FreeToolSharedDataRawData,
} from '@/Shared/AutoGenerated/Main'
import { RoutePathFreeToolEnum } from '@/Shared/AutoGenerated/RoutePathEnum'
import type { EnforceBEPagePropKeys } from '@/Shared/Helper/EnforceBEPagePropKeys'
import FreeToolSharedData from '@/FreeTool/Shared/Entity/FreeToolSharedData'
import BusinessNameGeneratorSession from '@/FreeTool/BusinessNameGenerator/Entity/BusinessNameGeneratorSession'
import { UrlFactory } from '@/Shared/Url/UrlFactory'

type BusinessNameGeneratorStepResultsPageProps = EnforceBEPagePropKeys<
    BEShowBusinessNameGeneratorStepResultsPagePropKeys,
    {
        freeToolSharedDataRawData: FreeToolSharedDataRawData
        businessNameGeneratorSessionRawData: BusinessNameGeneratorSessionRawData
    }
>

export default function BusinessNameGeneratorStepResultsPage({
    freeToolSharedDataRawData,
    businessNameGeneratorSessionRawData,
}: BusinessNameGeneratorStepResultsPageProps) {
    const sharedData = FreeToolSharedData.hydrate(freeToolSharedDataRawData)

    const businessNameGeneratorSession =
        BusinessNameGeneratorSession.hydrateSingle(
            businessNameGeneratorSessionRawData,
        )

    return (
        <FreeToolLayout
            appName={sharedData.appName}
            flashStatus={sharedData.flashStatus}
            headerRight={
                <span className="text-xs text-gray-500 sm:text-sm">
                    Business name generator
                </span>
            }
            footerLeft={
                <Button
                    as="a"
                    href={RoutePathFreeToolEnum.BusinessNameGenerator_StepStart}
                    color="light"
                    iconName={IconNameEnum.ArrowLeft}
                >
                    Back
                </Button>
            }
        >
            <BusinessNameGeneratorContainer
                eyebrow="Your name ideas"
                title="Which one feels like you?"
                description="Choose a name to create your booking page and a starter service."
            >
                <div className="mx-auto max-w-2xl">
                    {businessNameGeneratorSession.userPrompt ? (
                        <BusinessNameGeneratorUserPromptSummary
                            userPrompt={businessNameGeneratorSession.userPrompt}
                        />
                    ) : null}

                    {businessNameGeneratorSession.hasGeneratedBusinessName() ? (
                        <div className="space-y-4">
                            {businessNameGeneratorSession.generatedBusinessNames.map(
                                (suggestedName, zeroBasedIndex) => (
                                    <BusinessNameGeneratorSuggestedNameCard
                                        key={suggestedName.businessName}
                                        generatedBusinessName={suggestedName}
                                        displayIndex={zeroBasedIndex + 1}
                                        previewUrl={new UrlFactory().makeBusinessNameGeneratorStepResultPreviewUrl(
                                            suggestedName,
                                        )}
                                    />
                                ),
                            )}
                        </div>
                    ) : (
                        <BusinessNameGeneratorEmptySuggestionsCard />
                    )}

                    <p className="mt-8 text-center text-xs text-gray-500">
                        Names are suggestions. Availability has not been
                        checked.
                    </p>
                </div>
            </BusinessNameGeneratorContainer>
        </FreeToolLayout>
    )
}
